



























































































































import { Component, Ref, Vue } from "vue-property-decorator";
import router from "@/router/router";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import InfoConfigPublishDialogComponent from "@/components/admin/infoConfig/InfoConfigPublishDialog.vue";
import { InfoConfigVersionRow } from "@/data/models/InfoConfigAdministration";
import { cachedInfoConfigVersions, listInfoConfigVersions } from "@/data/repository/InfoConfigAdministrationRepository";
import { readThroughCache } from "@/utils/AdminReadCache";
import { BeschreibungDraft, readDraft } from "@/utils/BeschreibungFile";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { isNamedPortalSysAdmin } from "@/permissions/UserPermissions";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import {
  getLastVisitedItem,
  LAST_VISITED_ITEM_BACKGROUND_STYLE,
  setLastVisitedItem,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import {
  filterInfoConfigRows,
  INFO_CONFIG_FILTERS,
  InfoConfigBadge,
  InfoConfigFilter,
  InfoConfigListRow,
  versionBadges,
} from "@/utils/InfoConfigAdministration";
import { ComponentName } from "@/utils/RouteGuards";
import { themed, themedFill, themedFillTextClass } from "@/utils/Theme";
import InfoConfigBadgeChipComponent, { GLOBAL_TARGET_CHIP_COLOR } from "@/components/admin/infoConfig/InfoConfigBadgeChip.vue";
import AdminRefreshButtonComponent from "@/components/admin/AdminRefreshButton.vue";

/**
 * Beschreibungen in the admin portal: every version of the project forms, newest first, with what
 * takes it as a target (the fleet, clients, test users); the retired ones and the legacy leak-detection
 * files behind their own filters. A named SysAdmin publishes a new version from here (NEUE VERSION).
 *
 * **Not the access boundary**: `listInfoConfigVersionsForAdministration` repeats the portal and
 * `MANAGE_CLIENTS` checks, and every change needs a named SysAdmin in IRES Cloud too.
 */
const DEFAULT_FILTER: InfoConfigFilter = "CURRENT";
/** Session storage key (see `SecureLocalStorage`) for the list's filter. */
const FILTER_STORAGE_KEY = "ADMIN_INFO_CONFIG_FILTER";

function parseFilter(value: string | null | undefined): InfoConfigFilter {
  return INFO_CONFIG_FILTERS.find((filter) => filter === value) ?? DEFAULT_FILTER;
}

@Component({
  // Explicit, so Scaffold's keep-alive still matches it once the build minifies the class name.
  name: "InfoConfigVersionListComponent",
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "admin-refresh-button": AdminRefreshButtonComponent,
    "publish-dialog": InfoConfigPublishDialogComponent,
    "content-state": ContentStateComponent,
    "info-config-badge-chip": InfoConfigBadgeChipComponent,
  },
})
export default class InfoConfigVersionListComponent extends Vue {
  @Ref("publishDialog")
  publishDialog!: InfoConfigPublishDialogComponent;

  room: InfoConfigVersionRow[] = [];
  leakDetection: InfoConfigVersionRow[] = [];
  globalRoomTarget: string | null = null;
  filter: InfoConfigFilter = DEFAULT_FILTER;
  isLoading = true;
  /** Showing the cached versions while IRES Cloud is asked again (`AdminReadCache.ts`). */
  isRefreshing = false;
  /** The versions on screen are the cached answer, not yet confirmed (or its refresh failed): no + meanwhile. */
  showsCachedList = false;
  loadError = false;
  /** The draft this browser keeps, if any, to continue it. */
  draft: BeschreibungDraft | null = null;

  readonly filters = INFO_CONFIG_FILTERS;
  readonly defaultFilter = DEFAULT_FILTER;
  /** The version opened last, read once from session storage (Vue cannot track storage). */
  lastVisitedKey: string | null = null;
  /** Set on arrival, so the first load brings the version opened last back into view; a refresh does not. */
  scrollPending = false;
  /** False while kept alive but not shown; gates the app bar portal. */
  componentActive = false;

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  /** Unique across both kinds; also the last-opened key. */
  rowKey(row: InfoConfigListRow): string {
    return `infoConfig:${row.kind}:${row.version}`;
  }

  onFilterChanged(filter: InfoConfigFilter) {
    this.filter = filter;
    setValue(FILTER_STORAGE_KEY, filter);
  }

  get rows(): InfoConfigListRow[] {
    return filterInfoConfigRows(this.room, this.leakDetection, this.filter);
  }

  get isSysAdmin(): boolean {
    return isNamedPortalSysAdmin(getReactiveActiveUser());
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(row: InfoConfigListRow, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return this.rowKey(row) === this.lastVisitedKey ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  filterName(filter: InfoConfigFilter): string {
    return this.$t(`admin.infoConfig.filters.${filter}`).toString();
  }

  /** Below 600 px: the compact header (`AdminRefreshButton`, the global target on its own line). */
  get isPhone(): boolean {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The header's global target chip, filled green like the rows' (`InfoConfigBadgeChip`). */
  get globalTargetColor(): string {
    return themedFill(GLOBAL_TARGET_CHIP_COLOR);
  }

  get globalTargetTextClass(): string {
    return themedFillTextClass(GLOBAL_TARGET_CHIP_COLOR, "white--text");
  }

  /** The phone's text line: every badge but the global target, which the green icon shows. */
  phoneBadges(row: InfoConfigVersionRow): InfoConfigBadge[] {
    return versionBadges(row).filter((badge) => !badge.highlight);
  }

  badgeText(badge: InfoConfigBadge): string {
    return (badge.count === undefined ? this.$t(badge.key) : this.$tc(badge.key, badge.count)).toString();
  }

  badges(row: InfoConfigVersionRow): InfoConfigBadge[] {
    return versionBadges(row);
  }

  formatDate(millis: number | null): string {
    return millis === null ? "–" : formatDateToDayMonthYear(new Date(millis)) ?? "–";
  }

  open(row: InfoConfigListRow) {
    this.lastVisitedKey = this.rowKey(row);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.openVersion(row.kind, row.version);
  }

  openVersion(kind: string, version: string) {
    router.push({ name: ComponentName.INFO_CONFIG_VERSION, query: { kind, version } });
  }

  openDraft() {
    router.push({ name: ComponentName.INFO_CONFIG_DRAFT });
  }

  async onPublish() {
    const published = await this.publishDialog.show(this.room);
    if (published) {
      router.push({ name: ComponentName.INFO_CONFIG_VERSION, query: { kind: "ROOM", version: published } });
    }
  }

  /** The cached versions at once when they were read before in this tab (here or on the cockpit), then IRES Cloud's. */
  async load() {
    const cached = cachedInfoConfigVersions();
    this.isLoading = cached === null;
    this.isRefreshing = cached !== null;
    this.showsCachedList = cached !== null;
    this.loadError = false;
    try {
      await readThroughCache(cached, listInfoConfigVersions, (list, confirmed) => {
        this.room = list.room;
        this.leakDetection = list.leakDetection;
        this.globalRoomTarget = list.globalRoomTarget;
        this.showsCachedList = !confirmed;
        this.isLoading = false;
        this.scrollToLastVisited();
      });
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
      this.isRefreshing = false;
    }
  }

  /** Back from a version, bring it into view again once its row exists. */
  async scrollToLastVisited() {
    if (!this.scrollPending || !this.lastVisitedKey) {
      return;
    }
    this.scrollPending = false;
    await this.$nextTick();
    const row = Array.from(this.$el.querySelectorAll<HTMLElement>("[data-row-key]")).find(
      (element) => element.dataset.rowKey === this.lastVisitedKey
    );
    row?.scrollIntoView({ block: "center" });
  }

  /** Quiet refresh for a return from a version, which may have been published, retired or targeted. */
  async reloadInBackground() {
    try {
      const list = await listInfoConfigVersions();
      this.room = list.room;
      this.leakDetection = list.leakDetection;
      this.globalRoomTarget = list.globalRoomTarget;
      this.showsCachedList = false;
    } catch {
      // Swallowed on purpose: the rows shown stay valid, and the refresh button retries.
    }
  }

  mounted() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.infoConfig.title");
    this.draft = readDraft(window.localStorage);
    // A link may ask for a filter (the IRES Admin cockpit's figures do); it is applied and stored.
    // Otherwise the stored one, which survives for when the cached screen is dropped.
    const linked = this.$route.query.filter;
    if (typeof linked === "string") {
      this.onFilterChanged(parseFilter(linked));
    } else {
      this.filter = parseFilter(getValue(FILTER_STORAGE_KEY));
    }
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.scrollPending = true;
    this.load();
  }

  /**
   * Back from the cache: the rows at once with the version opened last in view, then a quiet refresh,
   * so a publish or retire made on the version's page still shows. The draft editor may have saved or
   * discarded the draft meanwhile.
   */
  activated() {
    this.componentActive = true;
    this.draft = readDraft(window.localStorage);
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    // Not on the first visit (activated follows mounted), where load() is still running.
    if (!this.isLoading && !this.isRefreshing && !this.loadError) {
      this.scrollPending = true;
      this.scrollToLastVisited();
      this.reloadInBackground();
    }
  }

  deactivated() {
    this.componentActive = false;
  }
}
